import React, { createContext, useContext, useRef, useState } from "react";
import styled from "@emotion/styled";
import { InlineProgress } from "~/components/inline-progress";
import { ValidationResponse } from "~/gql/types";
import { PriceClassification, ProductUnit } from "~/gql/types2";
import {
  GetAllProductsQuery,
  useGetAllProductsQuery,
} from "./GetAllProducts.hotchoc.generated";
import { useCreateProductMutation } from "./CreateProduct.hotchoc.generated";
import { useRenameProductMutation } from "./RenameProduct.hotchoc.generated";
import { useAddProductRateMutation } from "./AddProductRate.hotchoc.generated";
import { Product } from "./shared";

export type CreateProductArgs = {
  name: string;
  classification: PriceClassification;
  unit: ProductUnit;
};

export type RenameProductArgs = {
  productId: number;
  name: string;
};

export type AddProductRateArgs = {
  productId: number;
  amount: number;
};

/**
 * The products section's view of the server. Children read `products` as
 * settled data -- the provider withholds them until the first load resolves,
 * so no child has to model a "still loading" state or guess whether an empty
 * list means empty or pending.
 *
 * Mutations are promise-based and refetch before resolving, so a caller that
 * awaits one is guaranteed `products` already reflects the write. Callers stay
 * in their pending state for that extra round-trip rather than returning the
 * user to a list that has not caught up.
 */
type ProductsApi = {
  products: Product[];
  /**
   * Per-operation in-flight flags. Deliberately not one `saving` boolean: every
   * dialog on a page stays mounted whether open or closed, so a single flag
   * would spin the add-rate button during a rename. The initial load never
   * surfaces here -- children do not render until it settles.
   */
  saving: {
    creating: boolean;
    renaming: boolean;
    addingRate: boolean;
  };
  createProduct: (args: CreateProductArgs) => Promise<ValidationResponse>;
  renameProduct: (args: RenameProductArgs) => Promise<ValidationResponse>;
  addProductRate: (args: AddProductRateArgs) => Promise<ValidationResponse>;
};

const ProductsContext = createContext<ProductsApi>(null!);

const LoadingPane = styled("div")`
  display: flex;
  justify-content: center;
  padding: 48px 0;
`;

export const ProductsProvider: React.FC = ({ children }) => {
  const { data, refetch } = useGetAllProductsQuery();

  // The Apollo client runs `no-cache`, so a refetch blanks `data` while it is
  // in flight. Hold the last good result: without this the children unmount
  // mid-mutation, discarding open dialogs and firing "state update on an
  // unmounted component" when the success handler lands.
  const settled = useRef<GetAllProductsQuery | undefined>(undefined);

  if (data) {
    settled.current = data;
  }

  const loaded = settled.current;
  const [doCreate] = useCreateProductMutation();
  const [doRename] = useRenameProductMutation();
  const [doAddRate] = useAddProductRateMutation();

  // Tracked here rather than read off each mutation hook: those flags clear
  // when the write is answered, leaving the dialog looking idle while the
  // refetch it is still waiting on runs. These stay true for the whole
  // operation -- write plus refresh -- which is exactly what a caller awaits.
  const [creating, setCreating] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [addingRate, setAddingRate] = useState(false);

  const products = (loaded?.products?.all ?? []) as Product[];

  /**
   * Refreshes the catalog after a successful write and waits for it, so the
   * mutation only resolves once `products` reflects the change. The dialog
   * stays open a moment longer as a result -- deliberately: closing early
   * would drop the user onto a list that has not caught up yet.
   */
  const refreshAfter = async (
    response: ValidationResponse
  ): Promise<ValidationResponse> => {
    if (!response?.errors?.length) {
      await refetch();
    }

    return response;
  };

  const createProduct = async (
    args: CreateProductArgs
  ): Promise<ValidationResponse> => {
    setCreating(true);
    try {
      const result = await doCreate({ variables: { input: args } });

      return await refreshAfter(
        result?.data?.products?.createProduct as ValidationResponse
      );
    } finally {
      setCreating(false);
    }
  };

  const renameProduct = async (
    args: RenameProductArgs
  ): Promise<ValidationResponse> => {
    setRenaming(true);
    try {
      const result = await doRename({ variables: { input: args } });

      return await refreshAfter(
        result?.data?.products?.renameProduct as ValidationResponse
      );
    } finally {
      setRenaming(false);
    }
  };

  const addProductRate = async (
    args: AddProductRateArgs
  ): Promise<ValidationResponse> => {
    setAddingRate(true);
    try {
      const result = await doAddRate({ variables: { input: args } });

      return await refreshAfter(
        result?.data?.products?.addProductRate as ValidationResponse
      );
    } finally {
      setAddingRate(false);
    }
  };

  // Only the very first load blocks: once anything has settled, children stay
  // mounted through every subsequent refetch.
  if (!loaded) {
    return (
      <LoadingPane data-testid="products-loading">
        <InlineProgress />
      </LoadingPane>
    );
  }

  return (
    <ProductsContext.Provider
      value={{
        products,
        saving: { creating, renaming, addingRate },
        createProduct,
        renameProduct,
        addProductRate,
      }}
    >
      {children}
    </ProductsContext.Provider>
  );
};

export function useProducts(): ProductsApi {
  const context = useContext(ProductsContext);

  if (!context) {
    throw new Error("Cannot call useProducts outside of a ProductsProvider");
  }

  return context;
}
